// src/components/kyc/ApplicantEntityInfoStep.tsx
import React from "react";
import GlobalTextInputField from "../../components/GlobalTextInputField";
import { Step1State } from "../../types/types";
import Info from "../../assets/info.svg";
import UploadTable from "./UploadTable";
import UploadTableSingle from "./UploadTableSingle";

type Props = {
    value: Step1State;
    onChange: (patch: Partial<Step1State>) => void;
    readOnly?: boolean;
    isAdmin?: boolean;
};

const Label: React.FC<React.PropsWithChildren> = ({ children }) => (
    <label className="text-[10px] font-semibold text-text-base">{children}</label>
);

const ApplicantEntityInfoStep: React.FC<Props> = ({
    value,
    onChange,
    readOnly,
    isAdmin
}) => {
    const regulatorDisabled = value.isRegulated !== "yes";

    // ---- helpers for array fields ----
    const updateAt = (key: keyof Step1State, idx: number, url: string) => {
        const arr = [...(value[key] as string[])];
        arr[idx] = url;
        onChange({ [key]: arr });
    };

    const removeAt = (key: keyof Step1State, idx: number) => {
        const arr = [...(value[key] as string[])];
        arr.splice(idx, 1);
        onChange({ [key]: arr });
    };

    const addUrl = (key: keyof Step1State, url: string) => {
        const arr = [...(value[key] as string[]), url];
        onChange({ [key]: arr });
    };

    const handleRegulatedChange = (next: "yes" | "no" | "") => {
        const clearingPatch =
            next === "yes"
                ? {}
                : {
                    regulatorName: "",
                    licenceNature: "",
                    licenceNumber: "",
                    dateOfIssue: "",
                    dateOfExpiration: "",
                    countryOfIssuance: ""
                };
        onChange({ isRegulated: next, ...clearingPatch });
    };

    return (
        <div className="space-y-4 bg-white">
            <p className="text-text-base text-[10px]">
                Provide the legal and registration details of the applying entity. This
                information must match official company registry records.
            </p>

            <div className="grid grid-cols-2 gap-x-4 gap-y-4">

                {/* BASIC INFO */}
                <GlobalTextInputField
                    title="Legal Entity Name"
                    placeholder="Enter name"
                    value={value.legalEntityName}
                    onChange={(e: any) => onChange({ legalEntityName: e.target.value })}
                    showBorder
                    disabled={readOnly}
                />

                <GlobalTextInputField
                    title="Doing Business As (DBA)"
                    placeholder="Enter DBA"
                    value={value.dba}
                    onChange={(e: any) => onChange({ dba: e.target.value })}
                    showBorder
                    disabled={readOnly}
                />

                <GlobalTextInputField
                    title="Legal Identifier Number (If Available)"
                    placeholder="Enter Identifier"
                    value={value.legalIdentifierNumber}
                    onChange={(e: any) =>
                        onChange({ legalIdentifierNumber: e.target.value })
                    }
                    showBorder
                    disabled={readOnly}
                />

                <GlobalTextInputField
                    title="Company Reg. No."
                    placeholder="Enter Company Reg no."
                    value={value.companyRegNo}
                    onChange={(e: any) => onChange({ companyRegNo: e.target.value })}
                    showBorder
                    disabled={readOnly}
                />

                <GlobalTextInputField
                    title="Country of Incorporation"
                    placeholder="Enter Country"
                    value={value.countryOfIncorporation}
                    onChange={(e: any) =>
                        onChange({ countryOfIncorporation: e.target.value })
                    }
                    showBorder
                    disabled={readOnly}
                />

                <GlobalTextInputField
                    title="Date of Incorporation"
                    type="date"
                    value={value.dateOfIncorporation}
                    onChange={(e: any) =>
                        onChange({ dateOfIncorporation: e.target.value })
                    }
                    showBorder
                    disabled={readOnly}
                />

                <GlobalTextInputField
                    title="Legal Address"
                    placeholder="Enter Legal Address"
                    value={value.legalAddress}
                    onChange={(e: any) => onChange({ legalAddress: e.target.value })}
                    showBorder
                    disabled={readOnly}
                    className="col-span-2"
                />

                <GlobalTextInputField
                    title="Business Address (If Different)"
                    placeholder="Enter Business Address"
                    value={value.businessAddress}
                    onChange={(e: any) =>
                        onChange({ businessAddress: e.target.value })
                    }
                    showBorder
                    disabled={readOnly}
                    className="col-span-2"
                />

                <GlobalTextInputField
                    title="VAT No."
                    placeholder="Enter VAT No."
                    value={value.vatNo}
                    onChange={(e: any) => onChange({ vatNo: e.target.value })}
                    showBorder
                    disabled={readOnly}
                />

                <GlobalTextInputField
                    title="Tax Identification No."
                    placeholder="Enter TAX Id"
                    value={value.taxIdentificationNo}
                    onChange={(e: any) =>
                        onChange({ taxIdentificationNo: e.target.value })
                    }
                    showBorder
                    disabled={readOnly}
                />

                {/* NATURE OF BUSINESS */}
                <div className="col-span-2 flex flex-col gap-1 text-[10px]">
                    <Label>NATURE OF BUSINESS</Label>
                    <textarea
                        rows={3}
                        className="p-3 rounded-md border border-border-subtle"
                        value={value.natureOfBusiness}
                        onChange={(e) =>
                            onChange({ natureOfBusiness: e.target.value })
                        }
                        placeholder="Describe Nature of Business"
                        disabled={readOnly}
                    />
                </div>

                <GlobalTextInputField
                    title="Website / URL"
                    value={value.websiteUrl}
                    placeholder="Enter Website URL"
                    onChange={(e: any) => onChange({ websiteUrl: e.target.value })}
                    showBorder
                    disabled={readOnly}
                    className="col-span-2"
                />

                {/* REGULATOR */}
                <div className="flex flex-col gap-1 text-[10px]">
                    <Label>IS THE ENTITY REGULATED?</Label>
                    <select
                        className="p-3 rounded-md border border-border-subtle"
                        value={value.isRegulated}
                        onChange={(e) =>
                            handleRegulatedChange(e.target.value as any)
                        }
                        disabled={readOnly}
                    >
                        <option value="">Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                    </select>
                </div>

                <GlobalTextInputField
                    title="Name of Regulator"
                    placeholder="Enter Regulator Name"
                    value={value.regulatorName}
                    onChange={(e: any) =>
                        onChange({ regulatorName: e.target.value })
                    }
                    showBorder
                    disabled={regulatorDisabled || readOnly}
                />

                <GlobalTextInputField
                    title="Nature of Licence"
                    placeholder="Enter Nature of Licence"
                    value={value.licenceNature}
                    onChange={(e: any) =>
                        onChange({ licenceNature: e.target.value })
                    }
                    showBorder
                    disabled={regulatorDisabled || readOnly}
                />

                <GlobalTextInputField
                    title="Licence Number"
                    placeholder="Enter Licence Number"
                    value={value.licenceNumber}
                    onChange={(e: any) =>
                        onChange({ licenceNumber: e.target.value })
                    }
                    showBorder
                    disabled={regulatorDisabled || readOnly}
                />

                <GlobalTextInputField
                    title="Date of Issue"
                    type="date"
                    value={value.dateOfIssue}
                    onChange={(e: any) =>
                        onChange({ dateOfIssue: e.target.value })
                    }
                    showBorder
                    disabled={regulatorDisabled || readOnly}
                />

                <GlobalTextInputField
                    title="Date of Expiration"
                    type="date"
                    value={value.dateOfExpiration}
                    onChange={(e: any) =>
                        onChange({ dateOfExpiration: e.target.value })
                    }
                    showBorder
                    disabled={regulatorDisabled || readOnly}
                />

                <GlobalTextInputField
                    title="Country of Issuance"
                    value={value.countryOfIssuance}
                    placeholder="Enter Country of Issuance"
                    onChange={(e: any) =>
                        onChange({ countryOfIssuance: e.target.value })
                    }
                    showBorder
                    disabled={regulatorDisabled || readOnly}
                />

                {/* ================= UPLOADS ================= */}
                <div className="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">

                    {/* CERTIFICATE OF INCORPORATION */}
                    <div className="flex flex-col gap-1 text-[10px]">
                        <Label>CERTIFICATE OF INCORPORATION</Label>
                        <div className="flex items-center gap-1">
                            <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                <img src={Info} alt="" className="w-3 h-3" />
                            </div>
                            <span className="text-[8px]">
                                Upload official company registration document
                            </span>
                        </div>
                        <UploadTableSingle
                            value={value.certificateOfIncorporationUrl}
                            onChange={(url) =>
                                onChange({ certificateOfIncorporationUrl: url })
                            }
                            readOnly={readOnly}
                        />
                    </div>

                    {/* ADDRESS PROOF */}
                    <div className="flex flex-col gap-1 text-[10px]">
                        <Label>ADDRESS PROOF</Label>
                        <div className="flex items-center gap-1">
                            <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                <img src={Info} alt="" className="w-3 h-3" />
                            </div>
                            <span className="text-[8px]">
                                Utility bill or lease deed dated in the last 3 months
                            </span>
                        </div>
                        <UploadTableSingle
                            value={value.addressProofUrl}
                            onChange={(url) =>
                                onChange({ addressProofUrl: url })
                            }
                            readOnly={readOnly}
                        />
                    </div>

                    {/* CHARTER DOCUMENTS */}
                    <div className="flex flex-col gap-1 text-[10px]">
                        <Label>CHARTER DOCUMENTS (MOA / AOA ETC)</Label>
                        <div className="flex items-center gap-1">
                            <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                <img src={Info} alt="" className="w-3 h-3" />
                            </div>
                            <span className="text-[8px]">
                                Upload Memorandum and Articles of Association
                            </span>
                        </div>
                        <UploadTable
                            values={value.charterDocumentsUrls}
                            onChange={(i, url) =>
                                updateAt("charterDocumentsUrls", i, url)
                            }
                            add={(url) => addUrl("charterDocumentsUrls", url)}
                            remove={(i) => removeAt("charterDocumentsUrls", i)}
                            readOnly={readOnly}
                        />
                    </div>

                    {/* SHAREHOLDERS LIST */}
                    <div className="flex flex-col gap-1 text-[10px]">
                        <Label>LIST OF SHAREHOLDERS (&gt;20% HOLDING)</Label>
                        <div className="flex items-center gap-1">
                            <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                <img src={Info} alt="" className="w-3 h-3" />
                            </div>
                            <span className="text-[8px]">
                                Upload document showing major shareholders
                            </span>
                        </div>
                        <UploadTable
                            values={value.shareholdersListUrls}
                            onChange={(i, url) =>
                                updateAt("shareholdersListUrls", i, url)
                            }
                            add={(url) => addUrl("shareholdersListUrls", url)}
                            remove={(i) => removeAt("shareholdersListUrls", i)}
                            readOnly={readOnly}
                        />
                    </div>

                    {/* DIRECTORS LIST */}
                    <div className="flex flex-col gap-1 text-[10px] ">
                        <Label>LIST OF DIRECTORS</Label>
                        <div className="flex items-center gap-1">
                            <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                <img src={Info} alt="" className="w-3 h-3" />
                            </div>
                            <span className="text-[8px]">
                                Upload document listing current directors
                            </span>
                        </div>
                        <UploadTable
                            values={value.directorsListUrls}
                            onChange={(i, url) => updateAt("directorsListUrls", i, url)}
                            add={(url) => addUrl("directorsListUrls", url)}
                            remove={(i) => removeAt("directorsListUrls", i)}
                            readOnly={readOnly}
                        />
                    </div>

                    {/* ADDITIONAL DOCUMENTS */}
                    <div className="flex flex-col gap-1 text-[10px] ">
                        <Label>ADDITIONAL DOCUMENTS</Label>
                        <div className="flex items-center gap-1">
                            <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                <img src={Info} alt="" className="w-3 h-3" />
                            </div>
                            <span className="text-[8px]">
                                Upload any additional documents relevant to your application
                            </span>
                        </div>
                        <UploadTable
                            values={value.additionalUrls}
                            onChange={(i, url) => updateAt("additionalUrls", i, url)}
                            add={(url) => addUrl("additionalUrls", url)}
                            remove={(i) => removeAt("additionalUrls", i)}
                            readOnly={readOnly}
                        />
                    </div>

                </div>

            </div>
        </div>
    );
};

export default ApplicantEntityInfoStep;
